/**
 * c-duo-toggle.js
 *
 * PDP "Home & Away" toggle (snippets/c-duo-toggle.liquid).
 *
 * When the toggle is on, the product form adds the Duo variant instead of the
 * current one. Horizon's product-form-component builds its request with
 * `new FormData(form)`, which fires a `formdata` event on the form, so the
 * variant id is swapped there. Quantity, selling plan and the rest of the
 * form pass through unchanged.
 *
 * Subscriptions: when a selling plan is selected and the Duo does not carry
 * that plan, the toggle hides (and stops swapping) until one-time is chosen.
 */

(() => {
  if (customElements.get('c-duo-toggle')) return;

  /** @param {HTMLFormElement} form */
  function selectedPlan(form) {
    const field = form.elements.namedItem('selling_plan');
    if (!field) return '';
    return /** @type {HTMLInputElement | RadioNodeList} */ (field).value || '';
  }

  class CDuoToggle extends HTMLElement {
    connectedCallback() {
      this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-duo-input]'));
      this.duoPlans = (this.dataset.duoPlans || '').split(',').filter(Boolean);

      this.sync = this.sync.bind(this);
      this.onFormData = this.onFormData.bind(this);

      // Capture phase: formdata may not bubble, and the form can be re-rendered.
      document.addEventListener('formdata', this.onFormData, true);
      // Recharge renders its own plan inputs; re-check after any change on the page.
      document.addEventListener('change', this.sync);
      document.addEventListener('click', this.sync);

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('formdata', this.onFormData, true);
      document.removeEventListener('change', this.sync);
      document.removeEventListener('click', this.sync);
    }

    get form() {
      const form = document.getElementById(this.dataset.formId || '');
      return form instanceof HTMLFormElement ? form : null;
    }

    /** True when the selected plan (if any) can be carried over to the Duo. */
    planAllowed(form) {
      const plan = selectedPlan(form);
      return !plan || this.duoPlans.includes(plan);
    }

    sync() {
      // Plan inputs update after the click that changes them.
      requestAnimationFrame(() => {
        const form = this.form;
        if (!form) return;
        this.hidden = !this.planAllowed(form);
      });
    }

    /** @param {FormDataEvent} event */
    onFormData(event) {
      const form = this.form;
      if (!form || event.target !== form) return;
      if (!this.input || !this.input.checked) return;
      if (!this.planAllowed(form)) return;

      event.formData.set('id', this.dataset.duoVariant || '');
    }
  }

  customElements.define('c-duo-toggle', CDuoToggle);
})();
